import React, { useState, useCallback, useMemo } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditSpecificationModal from 'components/admin/common/modal/product/EditSpecificationModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';

const Specification = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createSpecificationData,
        postError: creatingError,
        postLoading: creating,
        putData: updateSpecificationData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteSpecificationData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.SPECIFICATIONS);

    const { fetchData: productData } = useCrud(API_ENDPOINTS.PRODUCTS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteSpecification = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditSpecification = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteSpecification, modalData: specificationToDelete, openModal: openDeleteSpecification, closeModal: closedDeleteSpecification } = useModalState({
        onClose: handleCloseDeleteSpecification
    });

    const { isOpen: isOpenEditSpecification, modalData: selectedSpecification, openModal: openEditSpecification, closeModal: closedEditSpecification } = useModalState({
        onClose: handleCloseEditSpecification
    });

    const validationRules = [
        { field: 'key', method: 'required' },
        { field: 'value', method: 'required' },
    ];

    const productOptions = useMemo(() => {
        if (productData?.length > 0) {
            return productData.map(product => ({
                label: product.title,
                value: product.id
            }));
        } else {
            return [{ label: 'a', value: '1' }, { label: 'a', value: '3' }];
        }
    }, [productData]);

    const specificationOptions = useMemo(() => {
        if (data?.length > 0) {
            return data.map(specification => ({
                label: specification.key,
                value: specification.id
            }));
        } else {
            return [{ label: 'a', value: '1' }, { label: 'a', value: '3' }];
        }
    }, [data]);

    const handleModalSave = useCallback(async (updatedSpecification) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedSpecification, validationRules);
        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return;
        }

        try {
            if (updatedSpecification.id) {
                await updateSpecificationData(updatedSpecification.id, updatedSpecification);
                setSuccessMessage("Especificación actualizada con éxito.");
            } else {
                await createSpecificationData(updatedSpecification);
                setSuccessMessage("Especificación creada con éxito.");
            }
            closedEditSpecification();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateSpecificationData, createSpecificationData, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newSpecification = {
            key: '',
            value: ''
        };
        openEditSpecification(newSpecification);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (specificationToDelete) {
            try {
                await deleteSpecificationData(specificationToDelete.id);
                setSuccessMessage("Especificación eliminada con éxito.");
                refetch();
                closedDeleteSpecification();
            } catch (error) {
                console.error(error);
            }
        }
    }, [specificationToDelete, deleteSpecificationData, refetch]);

    const columns = useMemo(() => [
        { field: 'product_title', header: 'Nombre de Producto', sortable: true },
        { field: 'key', header: 'Clave', sortable: true },
        { field: 'value', header: 'Valor', sortable: true }
    ], []);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Especificaciones</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Especificación
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditSpecification}
                onDelete={openDeleteSpecification}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="key"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditSpecificationModal
                show={isOpenEditSpecification}
                specification={selectedSpecification}
                handleClose={closedEditSpecification}
                handleSave={handleModalSave}
                updating={updating || creating}
                errors={errors}
                requestError={creatingError || updatingError}
                specificationOptions={specificationOptions}
                productOptions={productOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteSpecification}
                onHide={closedDeleteSpecification}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Specification);